import React, { useEffect, useMemo, useState } from 'react';
import { Button, Container, Form, Modal, Table } from 'react-bootstrap';
import { FaCheck, FaDownload, FaFilePdf, FaPencilAlt, FaPrint } from 'react-icons/fa';
import Select from 'react-select';
import { toast } from 'react-toastify';
import ModalConfirmacao from '~/Components/ModalConfirmacao';
import {
  downloadAnexoPj,
  listarAnexosContratoPj,
  listarContratosPj,
  obterDetalhesContratoPj,
  reativarContratoPj,
} from '~/Services/Service';
import { formatarCnpj } from '../Prestadores/utils/formatters';
import ModalAddContrato from './components/ModalAddContrato';
import GridPagination from '../components/GridPagination';
import SortableHeader, {
  alternarOrdenacao,
  ordenarRegistros,
} from '../components/SortableHeader';
import '../styles.css';

const PAGE_SIZE = 10;

const data = valor => (valor ? new Date(valor).toLocaleDateString('pt-BR') : '-');

const colunasOrdenacao = {
  prestador: contrato => `${contrato.razao_social || ''} ${contrato.cnpj || ''}`,
  inicio: contrato => contrato.data_inicio || '',
  rescisao: contrato => contrato.data_rescisao || '',
  status: contrato => (contrato.data_rescisao ? 'Inativo' : 'Ativo'),
  anexo: contrato => Number(contrato.qtd_anexos || 0),
};

const Contratos = () => {
  const [contratos, setContratos] = useState([]);
  const [busca, setBusca] = useState('');
  const [prestadorFiltro, setPrestadorFiltro] = useState(null);
  const [statusFiltro, setStatusFiltro] = useState('todos');
  const [modal, setModal] = useState(false);
  const [contratoEdicao, setContratoEdicao] = useState(null);
  const [previewPdf, setPreviewPdf] = useState({ show: false, url: '', nome: '' });
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [confirmacaoReativar, setConfirmacaoReativar] = useState({ show: false, item: null });
  const [listaAnexos, setListaAnexos] = useState({ show: false, contrato: null, itens: [], carregando: false });
  const [ordenacao, setOrdenacao] = useState(null);

  const carregar = async () => {
    try {
      setLoading(true);
      const resultado = await listarContratosPj();
      setContratos(Array.isArray(resultado) ? resultado : []);
    } catch (err) {
      toast.error(err.message || 'Erro ao carregar contratos.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  useEffect(() => () => {
    if (previewPdf.url) window.URL.revokeObjectURL(previewPdf.url);
  }, [previewPdf.url]);

  const prestadorOptions = useMemo(() => {
    const razaoSocialPorCodigoPj = new Map();
    contratos.forEach(contrato => {
      if (contrato.codigo_pj != null && !razaoSocialPorCodigoPj.has(contrato.codigo_pj)) {
        razaoSocialPorCodigoPj.set(contrato.codigo_pj, contrato.razao_social || `PJ ${contrato.codigo_pj}`);
      }
    });
    return Array.from(razaoSocialPorCodigoPj.entries())
      .map(([codigoPj, razaoSocial]) => ({ value: codigoPj, label: razaoSocial }))
      .sort((primeiro, segundo) => primeiro.label.localeCompare(segundo.label));
  }, [contratos]);

  const filtrados = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return contratos.filter(contrato => {
      const casaTexto = !termo
        || `${contrato.razao_social || ''} ${contrato.cnpj || ''}`.toLowerCase().includes(termo);
      const casaPrestador = !prestadorFiltro || contrato.codigo_pj === prestadorFiltro.value;
      const casaStatus = statusFiltro === 'todos'
        || (statusFiltro === 'ativo' && !contrato.data_rescisao)
        || (statusFiltro === 'inativo' && !!contrato.data_rescisao);
      return casaTexto && casaPrestador && casaStatus;
    });
  }, [contratos, busca, prestadorFiltro, statusFiltro]);

  useEffect(() => {
    setPage(1);
  }, [busca, prestadorFiltro, statusFiltro]);

  const ordenarPor = key => {
    setOrdenacao(atual => alternarOrdenacao(atual, key));
    setPage(1);
  };

  const contratosOrdenados = useMemo(
    () => ordenarRegistros(filtrados, ordenacao, colunasOrdenacao),
    [filtrados, ordenacao]
  );

  const contratosPaginados = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return contratosOrdenados.slice(start, start + PAGE_SIZE);
  }, [contratosOrdenados, page]);

  const ultimoContratoPorPj = useMemo(() => {
    const ordenados = [...contratos].sort((a, b) => {
      const inicioA = a.data_inicio || '';
      const inicioB = b.data_inicio || '';
      if (inicioA !== inicioB) return inicioB.localeCompare(inicioA);
      return Number(b.codigo || 0) - Number(a.codigo || 0);
    });
    const result = {};
    ordenados.forEach(c => {
      if (result[c.codigo_pj] == null) result[c.codigo_pj] = c.codigo;
    });
    return result;
  }, [contratos]);

  const abrirListaAnexos = async contrato => {
    if (!contrato.qtd_anexos) return;
    setListaAnexos({ show: true, contrato, itens: [], carregando: true });
    try {
      const itens = await listarAnexosContratoPj(contrato.codigo);
      setListaAnexos({ show: true, contrato, itens: Array.isArray(itens) ? itens : [], carregando: false });
    } catch (err) {
      toast.error(err.message || 'Erro ao listar PDFs.');
      setListaAnexos({ show: false, contrato: null, itens: [], carregando: false });
    }
  };

  const fecharListaAnexos = () => setListaAnexos({ show: false, contrato: null, itens: [], carregando: false });

  const abrirPreviewPdf = async (codigoAnexo, codigoPj, nome) => {
    if (!codigoAnexo) return;
    try {
      const blob = await downloadAnexoPj(codigoAnexo, codigoPj);
      if (previewPdf.url) window.URL.revokeObjectURL(previewPdf.url);
      const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      setPreviewPdf({ show: true, url, nome: nome || `contrato-${codigoAnexo}.pdf` });
    } catch (err) {
      toast.error(err.message || 'Erro ao baixar anexo.');
    }
  };

  const fecharPreviewPdf = () => {
    if (previewPdf.url) window.URL.revokeObjectURL(previewPdf.url);
    setPreviewPdf({ show: false, url: '', nome: '' });
  };

  const imprimirPdf = () => {
    if (!previewPdf.url) return;
    window.open(previewPdf.url, '_blank', 'noopener,noreferrer');
  };

  const baixarPdf = () => {
    if (!previewPdf.url) return;
    const link = document.createElement('a');
    link.href = previewPdf.url;
    link.download = previewPdf.nome || 'contrato.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const abrirNovoContrato = () => {
    setContratoEdicao(null);
    setModal(true);
  };

  const abrirEdicaoContrato = async contrato => {
    // DW-0982 — Melhoria Modal Contrato: o modal precisa de tipo_remuneracao,
    // valores, percentuais e flag tem_fechamentos da última vigência no range.
    try {
      const detalhe = await obterDetalhesContratoPj(contrato.codigo);
      setContratoEdicao(detalhe);
      setModal(true);
    } catch (err) {
      toast.error(err && err.message ? err.message : 'Erro ao carregar contrato.');
    }
  };

  const abrirConfirmacaoReativar = contrato => {
    setConfirmacaoReativar({ show: true, item: contrato });
  };

  const confirmarReativacao = async confirmado => {
    const contrato = confirmacaoReativar.item;
    setConfirmacaoReativar({ show: false, item: null });
    if (!confirmado || !contrato) return;
    try {
      await reativarContratoPj({ codigo: contrato.codigo });
      toast.success('Contrato reativado.');
      carregar();
    } catch (err) {
      toast.error(err.message || 'Erro ao reativar contrato.');
    }
  };

  return (
    <div className="hpj-page">
      <Container fluid className="p-0">
        <div className="hpj-shell">
          <div className="hpj-panel">
            <div className="hpj-title-row">
              <div>
                <div className="hpj-eyebrow">Departamento Jurídico</div>
                <h1 className="hpj-title">Contratos PJ</h1>
                <div className="hpj-subtitle">{filtrados.length} contrato(s)</div>
              </div>
              <Button className="hpj-btn hpj-btn-primary" variant="primary" onClick={abrirNovoContrato}>
                + Novo Contrato
              </Button>
            </div>

            <div className="hpj-toolbar hpj-filter-row">
              <Form.Group className="hpj-filter-field hpj-filter-field--pj">
                <Form.Label>Prestador</Form.Label>
                <Select
                  classNamePrefix="hpj-user-select"
                  isClearable
                  isSearchable
                  options={prestadorOptions}
                  placeholder="Todos"
                  value={prestadorFiltro}
                  onChange={setPrestadorFiltro}
                />
              </Form.Group>
              <Form.Group className="hpj-filter-field">
                <Form.Label>Status</Form.Label>
                <Form.Control
                  as="select"
                  className="hpj-filter-control"
                  value={statusFiltro}
                  onChange={event => setStatusFiltro(event.target.value)}
                >
                  <option value="todos">Todos</option>
                  <option value="ativo">Ativo</option>
                  <option value="inativo">Inativo</option>
                </Form.Control>
              </Form.Group>
              <Form.Group className="hpj-filter-field hpj-filter-field--rescisao">
                <Form.Label>Buscar</Form.Label>
                <Form.Control
                  className="hpj-search"
                  placeholder="Buscar por prestador ou anexo"
                  value={busca}
                  onChange={event => setBusca(event.target.value)}
                />
              </Form.Group>
            </div>

            <div className="hpj-card hpj-card--overflow hpj-card--responsive-list">
              <div className="hpj-table-scroll">
                <Table className="hpj-table" responsive>
                  <thead>
                    <tr>
                      <SortableHeader sortKey="prestador" ordenacao={ordenacao} onSort={ordenarPor}>
                        Prestador
                      </SortableHeader>
                      <SortableHeader sortKey="inicio" ordenacao={ordenacao} onSort={ordenarPor}>
                        Início
                      </SortableHeader>
                      <SortableHeader sortKey="rescisao" ordenacao={ordenacao} onSort={ordenarPor}>
                        Rescisão
                      </SortableHeader>
                      <SortableHeader sortKey="status" ordenacao={ordenacao} onSort={ordenarPor}>
                        Status
                      </SortableHeader>
                      <SortableHeader sortKey="anexo" ordenacao={ordenacao} onSort={ordenarPor}>
                        Anexo
                      </SortableHeader>
                      <th>Ação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contratosPaginados.map(contrato => (
                      <tr key={contrato.codigo}>
                        <td>
                          <div className="hpj-main-text">{contrato.razao_social}</div>
                          <div className="hpj-muted">{formatarCnpj(contrato.cnpj)}</div>
                        </td>
                        <td>{data(contrato.data_inicio)}</td>
                        <td>{data(contrato.data_rescisao)}</td>
                        <td>
                          <span className={`hpj-badge ${contrato.data_rescisao ? 'hpj-badge--slate' : 'hpj-badge--green'}`}>
                            {contrato.data_rescisao ? 'Inativo' : 'Ativo'}
                          </span>
                        </td>
                        <td>
                          {contrato.qtd_anexos > 0 ? (
                            <Button
                              className="hpj-icon-btn hpj-icon-btn-pdf"
                              size="sm"
                              title="Ver PDFs"
                              variant="link"
                              onClick={() => abrirListaAnexos(contrato)}
                            >
                              <FaFilePdf /> {contrato.qtd_anexos}
                            </Button>
                          ) : (
                            <span className="hpj-muted"><FaFilePdf /> Sem PDF</span>
                          )}
                        </td>
                        <td>
                          <Button
                            className="hpj-icon-btn"
                            size="sm"
                            title="Editar"
                            variant="link"
                            onClick={() => abrirEdicaoContrato(contrato)}
                          >
                            <FaPencilAlt />
                          </Button>
                          {contrato.data_rescisao
                            && ultimoContratoPorPj[contrato.codigo_pj] === contrato.codigo && (
                              <Button
                                className="hpj-icon-btn hpj-icon-btn-success"
                                size="sm"
                                title="Reativar contrato"
                                variant="link"
                                onClick={() => abrirConfirmacaoReativar(contrato)}
                              >
                                <FaCheck />
                              </Button>
                            )}
                        </td>
                      </tr>
                    ))}
                    {!filtrados.length && (
                      <tr>
                        <td colSpan="6" className="text-center text-muted py-4">
                          {loading ? 'Carregando...' : 'Nenhum contrato encontrado'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
              <div className="hpj-mobile-list">
                {contratosPaginados.map(contrato => (
                  <div className="hpj-mobile-card" key={contrato.codigo}>
                    <div className="hpj-mobile-card-head">
                      <div className="hpj-mobile-title-block">
                        <div className="hpj-mobile-title">{contrato.razao_social}</div>
                        <div className="hpj-mobile-subtitle">{formatarCnpj(contrato.cnpj)}</div>
                      </div>
                      <span className={`hpj-badge ${contrato.data_rescisao ? 'hpj-badge--slate' : 'hpj-badge--green'}`}>
                        {contrato.data_rescisao ? 'Inativo' : 'Ativo'}
                      </span>
                    </div>
                    <div className="hpj-mobile-grid">
                      <div className="hpj-mobile-field">
                        <span>Início</span>
                        <strong>{data(contrato.data_inicio)}</strong>
                      </div>
                      <div className="hpj-mobile-field">
                        <span>Rescisão</span>
                        <strong>{data(contrato.data_rescisao)}</strong>
                      </div>
                      <div className="hpj-mobile-field hpj-mobile-field--full">
                        <span>PDFs</span>
                        <strong>{contrato.qtd_anexos > 0 ? `${contrato.qtd_anexos} PDF(s)` : 'Sem PDF'}</strong>
                      </div>
                    </div>
                    <div className="hpj-mobile-actions">
                      {contrato.qtd_anexos > 0 && (
                        <Button
                          className="hpj-mobile-action-btn hpj-mobile-action-btn--pdf"
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => abrirListaAnexos(contrato)}
                        >
                          <FaFilePdf className="mr-1" /> {contrato.qtd_anexos} PDF(s)
                        </Button>
                      )}
                      <Button
                        className="hpj-mobile-action-btn"
                        size="sm"
                        variant="outline-primary"
                        onClick={() => abrirEdicaoContrato(contrato)}
                      >
                        <FaPencilAlt className="mr-1" /> Editar
                      </Button>
                      {contrato.data_rescisao
                        && ultimoContratoPorPj[contrato.codigo_pj] === contrato.codigo && (
                          <Button
                            className="hpj-mobile-action-btn hpj-mobile-action-btn--success"
                            size="sm"
                            variant="outline-secondary"
                            onClick={() => abrirConfirmacaoReativar(contrato)}
                          >
                            <FaCheck className="mr-1" /> Reativar
                          </Button>
                        )}
                    </div>
                  </div>
                ))}
                {!filtrados.length && (
                  <div className="hpj-mobile-empty">
                    {loading ? 'Carregando...' : 'Nenhum contrato encontrado'}
                  </div>
                )}
              </div>
              <GridPagination
                page={page}
                pageSize={PAGE_SIZE}
                totalItems={filtrados.length}
                onChange={setPage}
              />
            </div>
          </div>
        </div>
      </Container>

      <ModalAddContrato
        show={modal}
        onHide={() => setModal(false)}
        contrato={contratoEdicao}
        onSalvo={() => {
          setModal(false);
          setContratoEdicao(null);
          carregar();
        }}
        onAtualizado={carregar}
      />
      <ModalConfirmacao
        show={confirmacaoReativar.show}
        title="Reativar contrato"
        message="Confirma reativar este contrato? Ele volta a vigente e o prestador é reativado caso esteja rescindido."
        onClose={confirmarReativacao}
      />
      <Modal
        show={previewPdf.show}
        onHide={fecharPreviewPdf}
        centered
        className="hpj-modal hpj-modal-pdf"
      >
        <Modal.Header closeButton>
          <Modal.Title>Preview do contrato</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="hpj-pdf-preview">
            {previewPdf.url && (
              <iframe
                title={previewPdf.nome || 'Preview do contrato'}
                src={previewPdf.url}
                className="hpj-pdf-frame"
              />
            )}
          </div>
          <div className="hpj-pdf-name">{previewPdf.nome}</div>
        </Modal.Body>
        <Modal.Footer>
          <Button className="hpj-btn hpj-btn-outline" variant="light" onClick={imprimirPdf}>
            <FaPrint /> Imprimir
          </Button>
          <Button className="hpj-btn hpj-btn-primary" variant="primary" onClick={baixarPdf}>
            <FaDownload /> Baixar
          </Button>
          <Button className="hpj-btn" variant="light" onClick={fecharPreviewPdf}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={listaAnexos.show} onHide={fecharListaAnexos} centered className="hpj-modal">
        <Modal.Header closeButton>
          <Modal.Title>PDFs do contrato</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {listaAnexos.carregando && <div className="hpj-muted">Carregando...</div>}
          {!listaAnexos.carregando && !listaAnexos.itens.length && (
            <div className="hpj-muted">Nenhum PDF anexado.</div>
          )}
          {listaAnexos.itens.map(anexo => (
            <div className="hpj-file-item" key={anexo.codigo}>
              <div className="hpj-file-item-icon">
                <FaFilePdf />
              </div>
              <div className="hpj-file-item-copy">
                <div className="hpj-file-item-name">{anexo.nome_arquivo}</div>
              </div>
              <Button
                className="hpj-btn hpj-btn-outline"
                size="sm"
                variant="light"
                onClick={() => abrirPreviewPdf(anexo.codigo, listaAnexos.contrato.codigo_pj, anexo.nome_arquivo)}
              >
                Visualizar
              </Button>
            </div>
          ))}
        </Modal.Body>
        <Modal.Footer>
          <Button className="hpj-btn" variant="light" onClick={fecharListaAnexos}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Contratos;
